import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Alert,
    Button,
    Card,
    DataTable,
    DataTableActionButton,
    DataTableActions,
    DataTableColumn,
    Modal,
    message,
    Row,
    Segmented,
    Spin,
} from "../../design-system";
import {
    BRIEF_VIEWS,
    BriefListItem,
    BriefStatus,
    BriefView,
    BriefViewCounts,
    BuyerBrief,
    EVALUATION_GOAL_LABELS,
    LegacyBriefListItem,
    SUPPLIER_TYPE_LABELS,
} from "../../types/BuyerBriefTypes";
import { listBriefs, deleteBrief, getBriefCounts } from "../../common/api/BuyerBriefClient";
import { deleteSupplier } from "../../common/api/SupplierClient";
import { DateTime } from "../../common/components/DateTime";
import BuyerBriefWizard from "./BuyerBriefWizard";
import { BriefIntakeSeed, readBriefIntakeSeed } from "./briefIntakeParams";
import {
    BriefActions,
    BriefRecommendationCell,
    BriefStatusCell,
    BriefSupplierCell,
    LegacyBriefSupplierCell,
} from "./components/BriefCells";
import classes from "./BuyerBriefListView.module.less";

const VIEW_LABELS: Record<BriefView, string> = {
    all: "All briefs",
    mine: "My vendors",
    researching: "Researching",
    earlier: "Earlier",
};

const EMPTY_DESCRIPTIONS: Record<BriefView, string> = {
    all: "No briefs yet.",
    // The filtered views are empty because of the filter, not because there is
    // nothing here, so say which slice came back empty rather than "no briefs".
    mine: "No briefs yet for vendors you do business with.",
    researching: "No briefs for vendors you are only researching.",
    earlier: "No earlier briefs.",
};

/**
 * Splits the four views into the ones this org has something behind and the ones
 * worth putting on screen.
 *
 * The vendor filter only earns its place when both sides are populated. If every
 * brief is linked to a vendor, or none is, the filter would be three buttons
 * where two return the same list and the third returns nothing. That is not a
 * hypothetical: vendorKey is only written when someone picks a vendor from the
 * intake typeahead and was never backfilled, so every org starts out entirely
 * unlinked and sees no filter until it uses the typeahead.
 *
 * `available` also drives which view we are allowed to sit on, which is why it
 * is separate from `options`: an org with only legacy briefs renders no control
 * at all but still has to land on Earlier rather than an empty All.
 */
function deriveViews(counts: BriefViewCounts | null): {
    available: BriefView[];
    options: { label: string; value: BriefView }[];
} {
    // Counts not in yet, or the read failed. Show no control rather than
    // guessing: the list itself defaults to `all` and hides nothing, so the
    // worst case is a plain unfiltered list, and a control that appears and then
    // reshapes itself a moment later is worse than one that arrives late.
    if (!counts) return { available: [...BRIEF_VIEWS], options: [] };

    const available: BriefView[] = [];
    if (counts.all > 0) {
        available.push("all");
        if (counts.mine > 0 && counts.researching > 0) {
            available.push("mine", "researching");
        }
    }
    if (counts.earlier > 0) available.push("earlier");

    return {
        available,
        // One option is not a choice.
        options:
            available.length > 1
                ? available.map((value) => ({ label: VIEW_LABELS[value], value }))
                : [],
    };
}

// A brief mid-pipeline updates its step server-side; poll so the card follows
// along. Slower than the 5s the old Pending tab used, because each tick is now a
// full list read rather than one cheap pending query.
const PROCESSING_POLL_MS = 10000;

const BuyerBriefListView: React.FC = () => {
    const [briefs, setBriefs] = useState<BriefListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardSeed, setWizardSeed] = useState<BriefIntakeSeed | undefined>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [view, setView] = useState<BriefView>("all");
    // null until the first counts read lands. Distinct from all-zero, which
    // means "this org has no briefs at all"; null means "not known yet".
    const [counts, setCounts] = useState<BriefViewCounts | null>(null);

    // The view behind what is on screen, written synchronously when a load is
    // issued, so the poll chain and the row handlers reload the view the user
    // is looking at rather than the one a stale render remembers.
    const loadedView = useRef<BriefView>("all");

    useEffect(() => {
        loadData();
        loadCounts();
    }, []);

    // Arriving from a "Generate Buyer Brief" action: open pre-filled, then drop
    // the params so a refresh or a back-navigation doesn't reopen the wizard.
    useEffect(() => {
        const seed = readBriefIntakeSeed(searchParams);
        if (!seed) return;
        setWizardSeed(seed);
        setWizardOpen(true);
        setSearchParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async (
        briefView = loadedView.current,
        // The progress poll re-fetches every 10 seconds for the ~11 minutes a
        // brief takes. Raising the full-page Spin for each of those ticks made
        // the whole list flash roughly 66 times per generation, over rows that
        // are not changing. Reserve the overlay for loads the user asked for:
        // first paint, tab change, search.
        { showSpinner = true }: { showSpinner?: boolean } = {}
    ) => {
        if (showSpinner) setLoading(true);
        // Claim the view before awaiting, so a poll that fires mid-request still
        // follows the view the user asked for.
        loadedView.current = briefView;
        try {
            // The view is the server's filter: the whole of it comes back and
            // the table's search box narrows it in the browser.
            const briefsData = await listBriefs(briefView);
            setBriefs(briefsData.data || []);
        } catch {
        } finally {
            if (showSpinner) setLoading(false);
        }
    };

    const loadCounts = async () => {
        try {
            setCounts(await getBriefCounts());
        } catch {
            // Leaving `counts` null means no view control, and the list falls
            // back to `all`, which hides nothing. Failing towards "show
            // everything, offer no filter" is the safe direction.
        }
    };

    const handleViewChange = (next: BriefView) => {
        setView(next);
        loadData(next);
    };

    // Counts are unaffected by the search term, so they only need refreshing
    // when the set of briefs actually changes: a delete, a new brief, or a
    // generation finishing.
    const { available, options } = deriveViews(counts);

    // A view that empties out from under the user (deleting the last brief in
    // it, or the filter collapsing once one side goes to zero) would otherwise
    // leave the list querying something no longer offered, showing an empty page
    // with no way back. Keyed on `available` rather than `options` so an org
    // holding only legacy briefs still lands on Earlier, even though it renders
    // no control to click.
    useEffect(() => {
        if (!counts || available.length === 0) return;
        if (available.includes(view)) return;
        handleViewChange(available[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [counts]);

    // Re-runs whenever `briefs` changes, so each poll schedules the next one and
    // the chain stops on its own once nothing is in flight.
    //
    // QUEUED counts as in flight: the worker pool is finite, so a submitted
    // batch sits queued for a long time and would otherwise never repaint into
    // processing without a manual refresh.
    const hasProcessing = briefs.some(
        (b) =>
            b.source === "v2" &&
            (b.status === BriefStatus.PROCESSING || b.status === BriefStatus.QUEUED)
    );
    useEffect(() => {
        if (!hasProcessing) return;
        const timer = setTimeout(() => {
            loadData(loadedView.current, { showSpinner: false });
        }, PROCESSING_POLL_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [briefs, hasProcessing]);

    const handleDelete = (brief: BuyerBrief) => {
        Modal.confirm({
            title: "Delete Brief",
            content: `Are you sure you want to delete the brief for "${brief.supplierName}"?`,
            onOk: async () => {
                try {
                    await deleteBrief(brief.id);
                    message.success("Brief deleted");
                    loadData();
                    loadCounts();
                } catch {
                    message.error("Failed to delete brief");
                }
            },
        });
    };

    const handleDeleteLegacy = (brief: LegacyBriefListItem) => {
        Modal.confirm({
            title: "Delete Earlier Brief",
            content: `Are you sure you want to delete the brief for "${brief.supplierName}"?`,
            onOk: async () => {
                try {
                    await deleteSupplier(brief.id);
                    message.success("Brief deleted");
                    loadData();
                    loadCounts();
                } catch {
                    message.error("Failed to delete brief");
                }
            },
        });
    };

    // Land on `all`, the one view guaranteed to hold the brief just created
    // whether or not it picked up a vendor key, so the user never creates a
    // brief and then fails to find it.
    const handleWizardComplete = () => {
        setWizardOpen(false);
        setWizardSeed(undefined);
        setView("all");
        loadData("all");
        // This brief may be the first linked one, which is what makes the vendor
        // filter appear at all.
        loadCounts();
    };

    const created = (date?: string) =>
        date ? <DateTime date={new Date(date)} showTime={false} /> : "–";

    // Pre-V2 briefs carry no type, goal, status or recommendation: their
    // description takes that room. Which columns show follows the rows' own
    // `source`, so a response landing after a view switch still renders
    // coherently; a page holds one kind or the other.
    const legacyRows = briefs.length > 0 && briefs[0].source === "legacy";

    const v2Columns: DataTableColumn<BriefListItem>[] = [
        {
            key: "supplier",
            title: "Supplier",
            width: "20%",
            render: (b) => (b.source === "v2" ? <BriefSupplierCell brief={b} /> : null),
        },
        {
            key: "type",
            title: "Category",
            ellipsis: true,
            render: (b) => (b.source === "v2" ? SUPPLIER_TYPE_LABELS[b.supplierType] : null),
        },
        {
            key: "goal",
            title: "Goal",
            ellipsis: true,
            render: (b) => (b.source === "v2" ? EVALUATION_GOAL_LABELS[b.evaluationGoal] : null),
        },
        {
            key: "status",
            title: "Status",
            render: (b) => (b.source === "v2" ? <BriefStatusCell brief={b} /> : null),
        },
        {
            key: "recommendation",
            title: "Recommendation",
            render: (b) => (b.source === "v2" ? <BriefRecommendationCell brief={b} /> : null),
        },
        {
            key: "created",
            title: "Created",
            render: (b) => created(b.recordCreationDate),
        },
        {
            key: "actions",
            title: "Actions",
            actions: true,
            render: (b) =>
                b.source === "v2" ? (
                    <BriefActions brief={b} onDelete={handleDelete} onReRun={() => loadData()} />
                ) : null,
        },
    ];

    const legacyColumns: DataTableColumn<BriefListItem>[] = [
        {
            key: "supplier",
            title: "Supplier",
            width: "20%",
            render: (b) => (b.source === "legacy" ? <LegacyBriefSupplierCell brief={b} /> : null),
        },
        {
            key: "about",
            title: "About",
            ellipsis: true,
            render: (b) => (b.source === "legacy" ? b.description || "–" : null),
        },
        {
            key: "created",
            title: "Created",
            render: (b) => created(b.recordCreationDate),
        },
        {
            key: "actions",
            title: "Actions",
            actions: true,
            render: (b) =>
                b.source === "legacy" ? (
                    <DataTableActions>
                        <DataTableActionButton
                            icon="trash"
                            label="Delete brief"
                            onClick={() => handleDeleteLegacy(b)}
                        />
                    </DataTableActions>
                ) : null,
        },
    ];

    const content = (
        <Spin spinning={loading}>
            {view === "earlier" && briefs.length > 0 && (
                <Alert
                    className={classes.legacyDivider}
                    type="info"
                    showIcon
                    compact
                    message={
                        <>
                            <strong>Earlier briefs.</strong> Created before buyer briefs included
                            evaluation goals and recommendations.
                        </>
                    }
                />
            )}
            <DataTable<BriefListItem>
                columns={legacyRows ? legacyColumns : v2Columns}
                rows={briefs}
                // Ids collide across il_buyer_brief and il_supplier, so keys
                // are source-qualified.
                rowKey={(b) => `${b.source}-${b.id}`}
                // The columns carry no sort keys yet.
                sortable={false}
                controls={
                    /* One control for both axes. Three segments when the vendor
                       filter is meaningful, plus Earlier when the org has legacy
                       briefs, and nothing at all when there is only one thing to
                       look at. Picking Earlier drops the vendor filter, which is
                       correct: legacy briefs carry no vendor key to filter on. */
                    options.length > 0 ? (
                        <Segmented
                            options={options}
                            value={view}
                            onChange={(val) => handleViewChange(val as BriefView)}
                        />
                    ) : undefined
                }
                searchText={(b) => b.supplierName}
                searchPlaceholder="Search suppliers"
                emptyText={loading ? undefined : EMPTY_DESCRIPTIONS[view]}
            />
        </Spin>
    );

    return (
        <Card bordered={false}>
            <Row justify="space-between" className={classes.header}>
                <div>
                    <div className={classes.title}>Buyer Briefs</div>
                    <div className={classes.subtitle}>
                        Vendor diligence reports covering market position, competitive landscape,
                        and risk factors for each vendor.
                    </div>
                </div>
                <Button
                    type="primary"
                    size="large"
                    className={classes.creator}
                    onClick={() => setWizardOpen(true)}
                >
                    + New Buyer Brief
                </Button>
            </Row>
            {content}
            <BuyerBriefWizard
                open={wizardOpen}
                initialData={wizardSeed}
                onClose={() => {
                    setWizardOpen(false);
                    setWizardSeed(undefined);
                }}
                onComplete={handleWizardComplete}
            />
        </Card>
    );
};

export default BuyerBriefListView;
